import { DEFAULT_LANGUAGE } from "@next-platform/client-common-core";
import { useCallback } from "react";

import useLocalizedLocation from "./useLocalizedLocation";

export function useLocalizedPathname() {
  const { languageCode, isSupportedLang } = useLocalizedLocation();
  const activeLang = languageCode ?? DEFAULT_LANGUAGE;

  return useCallback(
    (pathname: string, customLang?: string) => {
      const lang = customLang ?? activeLang;
      if (isAbsoluteUrl(pathname) || isHashOnly(pathname) || isQueryOnly(pathname) || isDotRelativePath(pathname)) {
        return pathname;
      }
      if ((!customLang && !isSupportedLang) || lang === DEFAULT_LANGUAGE) {
        return pathname;
      }
      return buildLocalizedPath(pathname, lang);
    },
    [activeLang, isSupportedLang],
  );
}

function buildLocalizedPath(pathname: string, lang: string) {
  if (!pathname) {
    return pathname;
  }
  if (pathname === "/") {
    return `/${lang}`;
  }
  const normalizedPath = pathname.startsWith("/") ? pathname : `/${pathname}`;

  return `/${lang}${normalizedPath}`;
}

function isAbsoluteUrl(value: string) {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

function isHashOnly(value: string) {
  return value?.startsWith("#");
}

function isQueryOnly(value: string) {
  return value?.startsWith("?");
}

function isDotRelativePath(value: string) {
  return value?.startsWith(".");
}
